import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary-hover active:brightness-95 shadow-sm',
  secondary:
    'bg-surface-raised text-foreground border border-border hover:border-border-strong hover:bg-surface-sunken',
  outline:
    'border border-border-strong text-foreground hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'text-muted-foreground hover:text-foreground hover:bg-surface-sunken',
  danger: 'bg-danger text-white hover:brightness-110 active:brightness-95 shadow-sm',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-md',
  md: 'h-9.5 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-lg',
  icon: 'size-9.5 rounded-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Render as the child element (e.g. a react-router <Link>) keeping these styles. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading, disabled, children, asChild, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      ref={ref}
      // A spinner replaces the icon rather than being added next to it, so the
      // button never changes width mid-action and shifts the layout under a
      // thumb that is already moving toward it.
      disabled={disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-[background-color,border-color,color,box-shadow] duration-150',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      {/*
        Slottable marks which child Slot should merge onto. Without it, Slot sees
        two children - the spinner slot and this one - and `React.Children.count`
        counts the `null` spinner, so the count is 2 even when nothing is
        loading. Slot accepts exactly one child and throws "Slot failed to slot
        onto its children", taking the whole page down with it. That made every
        `asChild` button a white screen.
      */}
      {asChild ? <Slot.Slottable>{children}</Slot.Slottable> : children}
    </Comp>
  );
});
